import type { Subscriber } from 'services/Subscriber';
import type CustomWindow from 'interfaces/Utils';
import type {
  DataLayerObject,
  DataLayerPageStatus,
  EventData,
  PageViewDataLayerObject,
  PushableObject,
  OptimizelyDecisionTracking,
} from './entities';
import type { CerosEvent } from 'interfaces/Ceros';
import type { VevEvent } from 'interfaces/Vev';

declare let window: CustomWindow;

const pushToDataLayer = (obj: PushableObject) => {
  window.dataLayer.push(obj);
};

const trackEvent = (data: EventData) => {
  const trackingData: DataLayerObject = {
    event: 'eventTracker',
    ...data,
    eventAction: data.eventAction?.toLowerCase() || '',
    eventLabel: data.eventLabel?.toLowerCase() || '',
    nonInteraction: 0,
  };

  if (data.eventCategory) {
    trackingData.eventCategory = data.eventCategory?.toLowerCase() || '';
  }

  if (data.contentPlacementCD) {
    trackingData.contentPlacementCD = data.contentPlacementCD?.toLowerCase() || '';
  }

  if (data.CompanyCD) {
    trackingData.CompanyCD = data.CompanyCD?.toLowerCase() || '';
  }

  if (data.rankingTitleCD) {
    trackingData.rankingTitleCD = data.rankingTitleCD?.toLowerCase() || '';
  }

  if (data.userIDCD) {
    trackingData.userIDCD = data.userIDCD?.toLowerCase() || '';
  }

  const pageViewData = window.dataLayer.find(
    (item) => (item as PageViewDataLayerObject).event === 'fortune.historyChange',
  );
  const optimizelyData = window.dataLayer.filter(
    (item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision',
  );
  const optimizelyVariationNames = optimizelyData
    .map((item) => (item as OptimizelyDecisionTracking).variationKey)
    .filter((name) => name !== undefined)
    .join(',');

  const cmsCategoryCD = (pageViewData as PageViewDataLayerObject)?.cmsCategoryCD?.toLowerCase() || '';

  pushToDataLayer({ ...trackingData, cmsCategoryCD, optimizely: [...optimizelyData], optimizelyVariationNames });
};

const GTMPageView = (data: PageViewDataLayerObject) => {
  const trackingData: PageViewDataLayerObject = {
    event: 'fortune.historyChange',
    ...data,
  };
  const optimizelyData = window.dataLayer.filter(
    (item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision',
  );
  const optimizelyVariationNames = optimizelyData
    .map((item) => (item as OptimizelyDecisionTracking).variationKey)
    .filter((name) => name !== undefined)
    .join(',');

  pushToDataLayer({ ...trackingData, optimizely: [...optimizelyData], optimizelyVariationNames });
};

export const getGtmPageStatusEvent = (
  isError: boolean,
  errorCode?: number,
  errorDetail?: string,
): DataLayerPageStatus => ({
  errorCode: errorCode ?? 0,
  errorDetail: errorDetail ?? '',
  event: `Page Status - ${isError ? `Error (${errorCode})` : 'Init'}`,
  isError,
});

// NOTE: This is currently unused. Keeping it here for reference in case we need it in the future.
// Specifically, we care about the shape of the datalayer objectl
// const gtmPurchaseTracker = async ({
//   rid,
//   uid,
//   termConversionId,
//   chargeAmount,
//   chargeCurrency,
//   termId,
// }: ConversionResponse) => {
//   pushToDataLayer({ ecommerce: null }); // Clear the previous ecommerce object.

//   const data = await paymentDimensions(rid, uid);
//   const optimizelyData = window.dataLayer.filter(
//     (item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision',
//   );
//   const optimizelyVariationNames = optimizelyData
//     .map((item) => (item as OptimizelyDecisionTracking).variationKey)
//     .filter((name) => name !== undefined)
//     .join(',');
//   pushToDataLayer({
//     billingCountryCD: data.billing_country,
//     ecommerce: {
//       purchase: {
//         actionField: {
//           affiliation: 'Fortune Media',
//           currency: chargeCurrency,
//           id: termConversionId,
//           revenue: chargeAmount,
//         },
//         products: [
//           {
//             category: 'Fortune Subscription',
//             currency: chargeCurrency,
//             id: termConversionId,
//             name: termId,
//             price: chargeAmount,
//           },
//         ],
//       },
//     },
//     event: 'eec.purchase',
//     optimizely: [...optimizelyData],
//     optimizelyVariationNames,
//     paymentMethodCD: data.payment_method,
//   });
// };

const subscriberPush = (subscriber: Subscriber) => {
  const digiohObj = {
    user: {
      accessLevel: null,
      userLoggedIn: !!subscriber.isLoggedIn,
    },
  };
  const optimizelyData = window.dataLayer.filter(
    (item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision',
  );
  const optimizelyVariationNames = optimizelyData
    .map((item) => (item as OptimizelyDecisionTracking).variationKey)
    .filter((name) => name !== undefined)
    .join(',');

  pushToDataLayer({ ...digiohObj, optimizely: [...optimizelyData], optimizelyVariationNames });
};

const userPush = (isloggedin: boolean) => {
  const user = { loginStatusCD: isloggedin ? 'logged-in' : 'guest' };
  pushToDataLayer(user);
};

const trackCerosEvent = (event: CerosEvent) => {
  const trackingData: EventData = {
    eventCategory: 'Ceros',
    eventAction: event.detail.eventType,
    ...event.detail,
  };
  trackEvent(trackingData);
};

const trackVevEvent = (event: VevEvent) => {
  const trackingData: EventData = {
    eventCategory: 'Vev',
    eventAction: event.detail.type,
    ...event.detail.data,
    ...event.detail.metaData,
  };
  trackEvent(trackingData);
};

export { trackEvent, pushToDataLayer, GTMPageView, subscriberPush, userPush, trackCerosEvent, trackVevEvent };
